import React from 'react';
/**
 * `SummaryProduct` renders a summary card for a selected product in a plan.
 *
 * It displays:
 * - Product name with optional remove/edit actions.
 * - Optional excess selection (when multiple excess options exist).
 * - Policy start/end dates (end date hidden for subscription products).
 * - Price information via `PriceDisplay`.
 *
 * Dates are derived from the JSON policy start date string and formatted using
 * the supplied `culture`.
 */

import { ProductType } from '../CommonTypes';
import { getExcessData, getSelectedVariant } from '../ProductMethods';
import PriceDisplay from '../PriceDisplay';
import { PaymentMethod } from '../Constants';
import { ButtonCommand } from '../Button/ButtonCommand';
import { CommonValidation } from '../CommonValidation/commonValidation';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../Formatter';
import { useCurrencySymbol } from '../../../hooks/useCurrencySymbol';

type SummaryProductProps = {
  product: ProductType;
  removeProduct?: (productTypeId: number) => void;
  showRemove: boolean;
  editProduct?: () => void;
  showEdit: boolean;
  culture: string;
};

const SummaryProduct = ({
  product,
  removeProduct,
  showRemove,
  editProduct,
  showEdit,
  culture
}: SummaryProductProps): JSX.Element => {
  const selectedVariant = getSelectedVariant(product.productVariants);
  const { displayExcessOptions } = getExcessData({
    productVariants: product.productVariants,
    tyreTypeId: product.selectedTyreTypeId,
    claimLimit: product.selectedClaimLimit,
    coverLevel: product.selectedCoverLevelId
  });
  const duration = parseInt(selectedVariant?.duration ?? '0');
  const { t } = useTranslation();
  const currencySymbol = useCurrencySymbol();

  const jsonStartDate = CommonValidation.getJsonDate(product.policyStartDate);

  const startDate = jsonStartDate ? new Date(jsonStartDate) : undefined;

  const getEndDate = () => {
    if (jsonStartDate) {
      const calculatedEndDate = new Date(jsonStartDate);
      calculatedEndDate.setMonth(calculatedEndDate.getMonth() + duration);
      calculatedEndDate.setDate(calculatedEndDate.getDate() - 1);

      return calculatedEndDate;
    }

    return undefined;
  };

  const endDate = getEndDate();

  const formattedStartDate = startDate
    ? startDate.toLocaleDateString(culture, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    : '';

  const formattedEndDate = endDate
    ? endDate.toLocaleDateString(culture, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    : '';

  const onRemoveProduct = () => {
    if (removeProduct) {
      removeProduct(product.productTypeId);
    }
  };

  return (
    <div className='container-item'>
      <div className='flex-row space-between'>
        <div>
          <div className='flex-row m1-bottom'>
            <div className='bold m05-top'>
              {selectedVariant?.customerFriendlyName ?? product.productName}
            </div>
            {showRemove === true && (
              <ButtonCommand
                name='btn-remove'
                text={t('summary.button-remove-text')}
                additionalClassNames='m1-left small'
                onClick={onRemoveProduct}
              />
            )}
            {editProduct && showEdit === true && (
              <ButtonCommand
                name='btn-remove'
                text={t('summary.button-edit-text')}
                additionalClassNames='m05-left small'
                onClick={editProduct}
              />
            )}
          </div>
          {displayExcessOptions && (
            <div className='mb-3'>
              {t('quotes-common.excess')}: {currencySymbol}
              {formatNumber(product?.selectedExcess ?? 0, 2, culture)}
            </div>
          )}
          <div className='flex-row mobile-to-column'>
            <p>
              {t('summary.quote-start-date', {
                formattedStartDate: formattedStartDate,
                interpolation: { escapeValue: false }
              })}
            </p>
            {selectedVariant?.paymentMethod !== PaymentMethod.Subscription && (
              <>
                <p className='m05-left m05-right mobile-hide'>-</p>
                <p>
                  {t('summary.quote-end-date', {
                    formattedEndDate: formattedEndDate,
                    interpolation: { escapeValue: false }
                  })}
                </p>
              </>
            )}
          </div>
        </div>
        <div className='mobile-top-margin'>
          <PriceDisplay
            product={product}
            noOfInstallments={selectedVariant?.noOfInstallments ?? 0}
            className='p3'
            selectedLinkedProducts={[]}
            culture={culture}
          />
        </div>
      </div>
      <hr />
    </div>
  );
};

export default SummaryProduct;
